import React from "react";
import { ArrowRight, Leaf } from "lucide-react";

const HERO_BG =
  "https://images.unsplash.com/photo-1663900108404-a05e8bf82cda?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwxfHxyaXlhZGglMjBza3lsaW5lJTIwbW9kZXJufGVufDB8fHx8MTc4MzY3MjM1MXww&ixlib=rb-4.1.0&q=85";

export default function Hero() {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0A1128] text-white"
    >
      {/* Background photo with dark overlay */}
      <div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${HERO_BG})` }}
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-[#0A1128]/75" aria-hidden="true" />
      <div className="absolute inset-0 bg-grid-navy opacity-40" aria-hidden="true" />

      {/* Animated circuit motif */}
      <svg
        className="absolute right-[-100px] top-[10%] w-[720px] h-[720px] opacity-30 hidden md:block"
        viewBox="0 0 600 600"
        aria-hidden="true"
      >
        <defs>
          <linearGradient id="glow" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#00D084" />
            <stop offset="100%" stopColor="#059669" />
          </linearGradient>
        </defs>
        <circle cx="300" cy="300" r="260" stroke="url(#glow)" strokeWidth="1" fill="none" opacity="0.6" />
        <circle cx="300" cy="300" r="180" stroke="#ffffff" strokeOpacity="0.2" strokeWidth="1" fill="none" />
        <circle cx="300" cy="300" r="100" stroke="#ffffff" strokeOpacity="0.15" strokeWidth="1" fill="none" />
        <path
          className="circuit-path"
          d="M60,300 L200,300 L240,260 L360,260 L400,300 L540,300"
          stroke="#00D084"
          strokeWidth="1.5"
          fill="none"
        />
        <path
          className="circuit-path"
          d="M300,60 L300,200 L340,240 L340,360 L300,400 L300,540"
          stroke="#ffffff"
          strokeOpacity="0.5"
          strokeWidth="1.2"
          fill="none"
        />
        {[60, 200, 400, 540].map((x, i) => (
          <circle key={`h-${i}`} cx={x} cy="300" r="4" fill="#00D084" />
        ))}
        {[60, 200, 400, 540].map((y, i) => (
          <circle key={`v-${i}`} cx="300" cy={y} r="4" fill="#ffffff" opacity="0.6" />
        ))}
        {/* Leaf overlay */}
        <path
          d="M240 320 C 260 250, 340 220, 400 240 C 410 300, 360 360, 280 360 C 250 360, 240 345, 240 320 Z"
          fill="#00D084"
          opacity="0.18"
        />
      </svg>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-24 min-h-[100svh] flex flex-col justify-center">
        <div className="max-w-3xl">
          <div className="reveal inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/20 bg-white/5 backdrop-blur-sm mb-8">
            <Leaf className="w-3.5 h-3.5 text-[#00D084]" />
            <span className="text-[11px] tracking-[0.24em] uppercase font-semibold text-white/85">
              Riyadh · Kingdom of Saudi Arabia
            </span>
          </div>

          <h1
            className="reveal delay-1 font-display text-5xl md:text-6xl lg:text-7xl font-medium leading-[1.02] tracking-tight"
            data-testid="hero-headline"
          >
            Strategic Technology
            <br />
            &amp; <span className="italic text-[#00D084]">Sustainable</span> Growth
            <br />
            Solutions.
          </h1>

          <p
            className="reveal delay-2 mt-8 text-lg md:text-xl text-white/75 max-w-2xl leading-relaxed"
            data-testid="hero-subheadline"
          >
            TAQAVED helps businesses transform ideas into investable, scalable and sustainable
            ventures across energy, industry, technology and emerging-market growth sectors.
          </p>

          <div className="reveal delay-3 mt-10 flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => scrollTo("services")}
              data-testid="hero-cta-explore"
              className="btn-primary px-7 py-4 rounded-full font-semibold inline-flex items-center justify-center gap-2"
            >
              Explore Services
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => scrollTo("contact")}
              data-testid="hero-cta-contact"
              className="btn-ghost px-7 py-4 rounded-full font-semibold"
            >
              Contact Us
            </button>
          </div>

          {/* Stats strip */}
          <div className="reveal delay-4 mt-16 grid grid-cols-3 gap-6 max-w-2xl">
            <div className="border-l border-white/15 pl-4">
              <div className="stat-number text-3xl md:text-4xl text-white">5</div>
              <div className="text-[11px] uppercase tracking-widest text-white/60 mt-1">
                Core practices
              </div>
            </div>
            <div className="border-l border-white/15 pl-4">
              <div className="stat-number text-3xl md:text-4xl text-white">8</div>
              <div className="text-[11px] uppercase tracking-widest text-white/60 mt-1">
                Sectors served
              </div>
            </div>
            <div className="border-l border-[#00D084]/60 pl-4">
              <div className="stat-number text-3xl md:text-4xl text-[#00D084]">KSA</div>
              <div className="text-[11px] uppercase tracking-widest text-white/60 mt-1">
                Market focus
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-b from-transparent to-[#0A1128]" />
    </section>
  );
}
